const { getElement, getElements } = window.BAO.Utils;
class PushCompatibleProducts extends window.BAO.CustomElement() {
  static get requiredAttributes() {
    return ["data-type", "data-product-handle"];
  }

  static get requiredElements() {
    return ["radio", "confirm"];
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.isLegacy) return;

    this.render();
  }

  setupListeners() {
    super.setupListeners();

    if (this.isLegacy) return;

    this.setupRadioChangeHandler();
    this.setupVariantChangeListener();
  }

  async render() {
    const html = await this.variationOptionHTML();

    if (!html.length) {
      this.remove();
      return;
    }

    this.els.options.element.innerHTML = html;

    this.afterRender();
  }

  afterRender() {
    this.setupRadioChangeHandler();
    this.setupVariantChangeListener();
  }

  setupRadioChangeHandler() {
    const radioEls = getElements(this.els.radio.selector, { context: this });

    radioEls.forEach((radioEl) => {
      this.listeners.add(radioEl, "change", this.handleChange.bind(this));

      if (!radioEl.value) {
        radioEl.checked = false;
      }
    });

    this.syncAddToCartButtons();
  }

  setupVariantChangeListener() {
    const productIdEl = getElement(
      "#product-form-template-product [data-product-id]",
    );
    if (!productIdEl) return;

    this.listeners.add(
      productIdEl,
      "change",
      this.syncAddToCartButtons.bind(this),
    );
  }

  handleChange(event) {
    this.syncAddToCartButtons();

    if (this.externalValueEl && event.target.dataset?.title) {
      this.externalValueEl.textContent = event.target.dataset.title;
    }

    this.els.confirm?.element?.click();
  }

  syncAddToCartButtons() {
    const isSelected = this.pendingElements.length === 0;

    this.addToCartButtons.forEach((button) => {
      this.updateAddToCartButton(button, isSelected);
    });
  }

  updateAddToCartButton(button, isSelected) {
    button.classList.toggle("btn-Button-disabled", !isSelected);

    if (isSelected) {
      button.removeAttribute("data-drawers-trigger");
      button.removeAttribute("aria-haspopup");

      /* Availability is managed by variants.js */
      if (button.disabled) return;

      button.type = "submit";
      this.setAddToCartButtonText(button, window.theme.strings.addToCart);

      return;
    }

    button.setAttribute("data-drawers-trigger", this.pendingDrawerKey);
    button.setAttribute("aria-haspopup", "dialog");
    button.type = "button";
    this.setAddToCartButtonText(button, this.pendingLabel);
  }

  setAddToCartButtonText(button, text) {
    const textEl =
      button.querySelector("[data-product-add-to-cart-text]") ?? button;
    textEl.textContent = text;
  }

  get pendingElements() {
    const radioSelector = this.els.radio.selector;

    return getElements("push-compatible-products").filter(
      (el) =>
        !getElements(radioSelector, { context: el }).some(
          (radioEl) => radioEl.checked,
        ),
    );
  }

  get pendingDrawerKey() {
    return this.pendingElements[0]?.drawerKey ?? this.drawerKey;
  }

  get pendingLabel() {
    const pendingEl = this.pendingElements[0];
    if (!pendingEl || pendingEl.dataset.type === "power_source") {
      return window.theme.strings.selectPowerSource;
    }

    return (
      pendingEl.externalValueEl?.textContent?.trim() ||
      window.theme.strings.selectPowerSource
    );
  }

  get addToCartButtons() {
    return getElements(
      '#product-form-template-product [data-product-add-to-cart], [form="product-form-template-product"][data-product-add-to-cart]',
    );
  }

  get drawerKey() {
    return `product-compatible-products-${this.dataset.type}`;
  }

  async variationOptionHTML() {
    const { type, productHandle } = this.dataset;

    const html =
      window.sessionStorage.getItem(this.storageKey) ||
      (await window.BAO.sectionRenderer.fetchSingle(
        `push-compatible-product-options-${type}`,
        `/products/${productHandle}`,
      ));

    window.sessionStorage.setItem(this.storageKey, html);

    return window.BAO.sectionRenderer.getInnerHtml(html);
  }

  get storageKey() {
    return `push-compatible-products-${this.dataset.productHandle}-${this.dataset.type}`;
  }

  get externalValueEl() {
    if (!this.externalEls?.value?.selector) return;

    return getElement(
      `${this.externalEls.value.selector}[data-type="${this.dataset?.type}"]`,
    );
  }

  get isLegacy() {
    return this.els?.options?.exists;
  }
}

if (!customElements.get("push-compatible-products")) {
  customElements.define("push-compatible-products", PushCompatibleProducts);
}
